import { HorizontalScrollView, ListItem } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useFacilityNotes } from "@src/appV2/Facilities/api/useFacilityNotes";
import { useHasTen99PolicyCoverage } from "@src/appV2/redesign/Documents/api/useHasTen99PolicyCoverage";
import { AnalyticsOpenFromValues } from "@src/appV2/redesign/IndependantContractorBadge/constants";
import { BaseIndependentContractorBadgeRequiredCard } from "@src/appV2/redesign/Shift/BottomSheet/BaseIndependentContractorBadgeRequiredCard";
import { Ten99PolicyShiftCoverageCard } from "@src/appV2/redesign/Shift/BottomSheet/Ten99PolicyShiftCoverageCard";
import { SHIFT_DISCOVERY_WORKPLACE_POLICIES_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { WorkplaceNoteIdentifier } from "@src/appV2/redesign/Workplace/constants";
import { useGetWorkplacesById } from "@src/appV2/redesign/Workplace/useGetWorkplacesById";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addHours, isAfter, parseISO } from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { WorkerShiftDetailsArrivalTimeCardContainer } from "./ArrivalTime/Container";
import { useShouldShowArrivalTimeCard } from "./ArrivalTime/useShouldShowArrivalTimeCard";
import { CancellationPolicyCard } from "./CancellationPolicyCard";
import { ChartingRequiredCard } from "./ChartingRequiredCard";
import { MandatoryBreakCard } from "./MandatoryBreakCard";
import { ParkingCard } from "./ParkingCard";
import { ShiftPaymentRequestCard } from "./ShiftPaymentRequestCard";
import { WorkerShiftDetailsUpdateEtaCardContainer } from "./UpdateEta/UpdateEtaCardContainer";
import { UploadTimesheetCard } from "./UploadTimesheetCard";

interface HorizontalCardWrapperListItemProps {
  shift: Shift;
  workerId: string;
  isMandatoryBreakEnabled: boolean;
  shiftState: ShiftStateData;
  isMissedPunchRequestEnabled: boolean;
}

// eslint-disable-next-line complexity
export function HorizontalCardWrapperListItem(props: HorizontalCardWrapperListItemProps) {
  const { shift, isMandatoryBreakEnabled, workerId, shiftState, isMissedPunchRequestEnabled } =
    props;

  const history = useHistory();
  const worker = useDefinedWorker();

  const { data: workplaceNotesData } = useFacilityNotes(shift?.facility.userId ?? "", {
    enabled: isDefined(shift?.facility.userId),
  });

  const { data: workplaces } = useGetWorkplacesById(
    { workplaceIds: shift.facility.userId ? [shift.facility.userId] : [] },
    { enabled: isDefined(shift.facility.userId) }
  );
  const isIndependentContractorBadgeRequired =
    workplaces?.pages.at(0)?.data.at(0)?.attributes.independentContractorBadge.enabled ?? false;

  const cancellationPolicyNote = workplaceNotesData?.find(
    (note) => note.identifier === WorkplaceNoteIdentifier.CANCEL_INSTRUCTIONS
  );

  const lastUpdatedAt = cancellationPolicyNote?.updatedAt ?? undefined;

  const isShiftMoreThan8hoursAway =
    shift.start && isAfter(parseISO(shift.start), addHours(new Date(), 8));

  const handleViewWorkplacePolicies = () => {
    history.push(
      generatePath(SHIFT_DISCOVERY_WORKPLACE_POLICIES_FULL_PATH, {
        workplaceId: shift.facility.userId,
      })
    );
  };

  const shouldShowArrivalTimeCard = useShouldShowArrivalTimeCard({ shift });

  const analyticsMetadata = {
    shiftId: shift._id,
    state: shift.facility.fullAddress?.state ?? "",
    workplaceId: shift.facility.userId ?? "",
    workplaceName: shift.facility.name ?? "",
    workerId: worker.userId,
    workerFullName: `${worker.firstName} ${worker.lastName}`,
    role: shift.agentReq,
    openedFrom: AnalyticsOpenFromValues.BOOKING_DETAILS,
    hasShiftWithBadgeRequired: isIndependentContractorBadgeRequired,
  };

  const hasTen99PolicyCoverage = useHasTen99PolicyCoverage({
    shiftQualification: shift.agentReq,
    workplaceId: shift.facility.userId,
  });

  return (
    <ListItem divider>
      <HorizontalScrollView
        stretch
        sx={{ px: 5, height: "max-content" }}
        outerPaddingCompensation={5}
      >
        {shouldShowArrivalTimeCard && <WorkerShiftDetailsArrivalTimeCardContainer shift={shift} />}
        <WorkerShiftDetailsUpdateEtaCardContainer shift={shift} />
        {isShiftMoreThan8hoursAway && isIndependentContractorBadgeRequired && (
          <BaseIndependentContractorBadgeRequiredCard
            history={history}
            shiftId={shift._id}
            state={shift.facility.fullAddress?.state ?? ""}
            qualification={shift.agentReq}
            analyticsMetadata={analyticsMetadata}
          />
        )}
        {shift.isInstantPay && (
          <ParkingCard shift={shift} workerId={workerId} shiftState={shiftState} />
        )}
        <ChartingRequiredCard shift={shift} workerId={workerId} />
        {!shift.isInstantPay && isMissedPunchRequestEnabled && <ShiftPaymentRequestCard />}
        {!shift.isInstantPay && !isMissedPunchRequestEnabled && <UploadTimesheetCard />}
        {!isShiftMoreThan8hoursAway && isIndependentContractorBadgeRequired && (
          <BaseIndependentContractorBadgeRequiredCard
            history={history}
            shiftId={shift._id}
            state={shift.facility.fullAddress?.state ?? ""}
            qualification={shift.agentReq}
            analyticsMetadata={analyticsMetadata}
          />
        )}
        <MandatoryBreakCard
          shift={shift}
          isMandatoryBreakEnabled={isMandatoryBreakEnabled}
          onViewWorkplacePolicies={handleViewWorkplacePolicies}
        />
        <CancellationPolicyCard
          workplaceLateCancellationPeriod={shift.facility.lateCancellation?.period}
          workplaceLateCancellationFeeHours={shift.facility.lateCancellation?.feeHours}
          workplaceSentHomeChargeHours={shift.facility.sentHomeChargeHours}
          lastUpdatedAt={isDefined(lastUpdatedAt) ? parseISO(lastUpdatedAt) : undefined}
          onViewWorkplacePolicies={handleViewWorkplacePolicies}
        />
        {hasTen99PolicyCoverage && <Ten99PolicyShiftCoverageCard />}
      </HorizontalScrollView>
    </ListItem>
  );
}
